/**
 * Respawn Logic — reset player state after stock loss, place at last safe ground.
 * Only active in modes with stocks (vs_local, vs_online).
 */

import { STOCKS, MOMENTUM, HOP, PHYSICS, VORTEX } from '../constants';
import { getGroundY } from '../terrain/grid';
import type { Player, GameState } from '../types';
import type { GameEffect } from './effects';

/**
 * Execute respawn for a player: reset position to last safe ground,
 * clear combat state, grant invulnerability, emit VFX.
 */
export function executeRespawn(state: GameState, playerIndex: number): GameEffect[] {
  const p = state.players[playerIndex];
  const fx: GameEffect[] = [];

  // Build list of alive opponents
  const opponents = state.players.filter((op, i) => i !== playerIndex && op.alive);

  // Score a candidate X by minimum distance to any alive opponent (higher = better)
  const minOpponentDist = (x: number): number => {
    if (opponents.length === 0) return Infinity;
    let min = Infinity;
    for (const op of opponents) {
      const d = Math.abs(x - op.x);
      if (d < min) min = d;
    }
    return min;
  };

  // Generate candidate spawn positions from stage-fraction presets
  const stageW = state.stageRight - state.stageLeft;
  const portalSafeDistance = VORTEX.TENTACLE_RANGE + 100;

  type Candidate = { x: number; y: number; dist: number };
  const candidates: Candidate[] = [];

  for (const frac of STOCKS.RESPAWN_POSITIONS) {
    const cx = state.stageLeft + stageW * frac;
    const gy = getGroundY(cx);

    // Skip positions that are in the void
    if (gy >= state.voidY - PHYSICS.ECB_RADIUS) continue;

    const cy = gy - PHYSICS.ECB_RADIUS;

    // Skip positions too close to vortex portals
    let portalSafe = true;
    for (const hole of state.vortexHoles) {
      const dx = cx - hole.x;
      const dy = cy - hole.y;
      if (Math.sqrt(dx * dx + dy * dy) < portalSafeDistance) { portalSafe = false; break; }
    }
    if (!portalSafe) continue;

    candidates.push({ x: cx, y: cy, dist: minOpponentDist(cx) });
  }

  let spawnX: number;
  let spawnY: number;

  if (candidates.length > 0) {
    // Pick the candidate farthest from all opponents
    candidates.sort((a, b) => b.dist - a.dist);
    spawnX = candidates[0].x;
    spawnY = candidates[0].y;
  } else {
    // All preset positions are in void or near portals — fall back to farthest
    // valid ground from opponents by scanning stage fractions without filters
    let bestX = (state.stageLeft + state.stageRight) / 2;
    let bestY = getGroundY(bestX) - PHYSICS.ECB_RADIUS;
    let bestDist = -1;
    for (const frac of STOCKS.RESPAWN_POSITIONS) {
      const cx = state.stageLeft + stageW * frac;
      const gy = getGroundY(cx);
      if (gy >= state.voidY - PHYSICS.ECB_RADIUS) continue;
      const d = minOpponentDist(cx);
      if (d > bestDist) { bestDist = d; bestX = cx; bestY = gy - PHYSICS.ECB_RADIUS; }
    }
    spawnX = bestX;
    spawnY = bestY;

    // If no valid ground found at all (entire stage in void), clamp above void line
    if (bestDist === -1) {
      spawnY = Math.min(spawnY, state.voidY - PHYSICS.ECB_RADIUS - 50);
    }
  }

  // Final safety: if spawn is in or near the void, clamp above void surface
  if (spawnY + PHYSICS.ECB_RADIUS >= state.voidY) {
    spawnY = state.voidY - PHYSICS.ECB_RADIUS * 3;
  }

  // Reset player state
  p.x = spawnX;
  p.y = spawnY;
  p.vx = 0;
  p.vy = 0;
  p.alive = true;
  p.pendingDeath = false;
  p.damagePercent = 0;
  p.momentum = MOMENTUM.MAX;
  p.momentumTurnStart = MOMENTUM.MAX;

  // Clear tumble / hitstun / ragdoll
  p.tumble = false;
  p.hitstunFrames = 0;
  p.launchArmor = 0;
  p.tumbleRecoveryFrame = 0;
  p.tumbleRecoveryMax = 0;
  p.rotation = 0;

  // Clear grapple state
  p.grappleLaunched = false;
  p.controlRestored = false;

  // Clear burst/DI state
  p.burstFlying = false;
  p.burstTrailCooldown = 0;
  p.burstBraced = false;
  p.diPendingVx = 0;
  p.diPendingVy = 0;
  p.diAngleShift = 0;
  p.kbStackHitCount = 0;
  p.kbStackFirstFrame = 0;

  // Clear defensive movement
  p.landingTimer = 0;
  p.landingTimerMax = 0;
  p.defenseSubPhase = 'none';

  // Clear hop/lift
  p.liftActive = false;
  p.liftFuel = HOP.MAX_LIFT_FUEL;
  p.fastFallActive = false;
  p.fastFallHeld = false;
  p.platformDropTimer = 0;
  p.onWireframe = false;

  // Settle at spawn position
  p.grounded = true;
  p.settled = true;
  p.settledX = spawnX;
  p.settledY = spawnY;
  p.actionState = { tag: 'settled', settledX: spawnX, settledY: spawnY };

  // Clear respawn timer, grant invulnerability
  p.respawnTimer = 0;
  p.respawnInvulnFrames = STOCKS.RESPAWN_INVULN_FRAMES;

  // VFX: particles + "RESPAWN!" world text
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [spawnX, spawnY, 0xffffff, 30] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [spawnX, spawnY, p.color, 20] });
  fx.push({ type: 'announce_world', text: 'RESPAWN!', x: spawnX, y: spawnY - 60, color: 0x44ffaa, fontSize: 36 });
  fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.15 });
  fx.push({ type: 'camera_follow', x: spawnX, y: spawnY });

  return fx;
}
